'use client';

// useClarificationQuestions - TanStack Query hook for clarification questions

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ClarificationQuestion,
  ClarificationQuestionsResponse,
  GetQuestionsResponse,
  SubmitAnswerResponse,
  SkipQuestionResponse,
  QuestionsSummary,
} from './types';

/**
 * Query key factory for clarification questions
 */
const queryKeys = {
  all: ['clarification-questions'] as const,
  questions: (botId: string) => [...queryKeys.all, botId] as const,
};

/**
 * Fetch existing questions from the session
 */
async function fetchQuestions(botId: string): Promise<ClarificationQuestion[]> {
  const response = await fetch(`/api/bots/${botId}/clarification-questions`);

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to fetch questions');
  }

  const result: GetQuestionsResponse = await response.json();
  return result.data.questions;
}

/**
 * Generate new clarification questions
 * @param existingQuestionTexts - Question texts to exclude from generation (for "generate more")
 */
async function generateQuestions(
  botId: string,
  existingQuestionTexts?: string[]
): Promise<ClarificationQuestionsResponse['data']> {
  const response = await fetch(`/api/bots/${botId}/clarification-questions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...(existingQuestionTexts?.length && { existingQuestionTexts }),
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to generate questions');
  }

  const result: ClarificationQuestionsResponse = await response.json();
  return result.data;
}

/**
 * Submit an answer to a question
 * Includes question data since server doesn't persist questions in memory
 */
async function submitAnswer(
  botId: string,
  questionId: string,
  answer: string,
  questionText: string,
  category: string,
  contentId?: string
): Promise<SubmitAnswerResponse['data']> {
  const response = await fetch(`/api/bots/${botId}/clarification-questions/answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      questionId,
      answer,
      questionText,
      category,
      ...(contentId ? { contentId } : {}),
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to submit answer');
  }

  const result: SubmitAnswerResponse = await response.json();
  return result.data;
}

/**
 * Skip a question
 */
async function skipQuestion(
  botId: string,
  questionId: string
): Promise<SkipQuestionResponse['data']> {
  const response = await fetch(`/api/bots/${botId}/clarification-questions/answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ questionId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error?.message || 'Failed to skip question');
  }

  const result: SkipQuestionResponse = await response.json();
  return result.data;
}

/**
 * Calculate summary from questions array
 */
function calculateSummary(questions: ClarificationQuestion[]): QuestionsSummary {
  const answered = questions.filter((q) => q.status === 'answered');
  const skipped = questions.filter((q) => q.status === 'skipped');
  const pending = questions.filter((q) => q.status === 'pending');

  return {
    total: questions.length,
    answered: answered.length,
    skipped: skipped.length,
    pending: pending.length,
    totalCharactersAdded: answered.reduce((sum, q) => sum + (q.answer?.length || 0), 0),
  };
}

export interface UseClarificationQuestionsOptions {
  botId: string;
  enabled?: boolean;
}

export interface UseClarificationQuestionsReturn {
  /** All questions (displayed ones only - high/medium priority) */
  questions: ClarificationQuestion[];
  /** Whether questions are being loaded */
  isLoading: boolean;
  /** Whether questions are being generated */
  isGenerating: boolean;
  /** Error if any */
  error: Error | null;
  /** Generate new questions (replaces existing) */
  generate: () => void;
  /** Generate additional questions (appends to existing) */
  generateMore: () => void;
  /** Submit an answer */
  submitAnswer: (questionId: string, answer: string) => void;
  /** Skip a question */
  skipQuestion: (questionId: string) => void;
  /** Reopen a resolved question (set back to pending) */
  reopenQuestion: (questionId: string) => void;
  /** Whether an answer is being submitted */
  isSubmitting: boolean;
  /** Whether all questions are resolved (answered or skipped) */
  allQuestionsResolved: boolean;
  /** Summary statistics */
  summary: QuestionsSummary;
  /** Content analysis metadata */
  contentAnalysis: ClarificationQuestionsResponse['data']['contentAnalysis'] | null;
  /** Whether existing questions were loaded from database (returning user) */
  hasExistingQuestions: boolean;
}

/**
 * Hook for managing clarification questions
 *
 * @param options - Hook options including botId
 * @returns Query state and mutation functions
 *
 * @example
 * ```tsx
 * const {
 *   questions,
 *   isLoading,
 *   generate,
 *   submitAnswer,
 *   skipQuestion,
 *   allQuestionsResolved,
 * } = useClarificationQuestions({ botId: 'bot-123' });
 *
 * // Generate questions
 * generate();
 *
 * // Submit an answer
 * submitAnswer('question-id', 'My answer');
 *
 * // Skip a question
 * skipQuestion('question-id');
 * ```
 */
export function useClarificationQuestions({
  botId,
  enabled = true,
}: UseClarificationQuestionsOptions): UseClarificationQuestionsReturn {
  const queryClient = useQueryClient();
  const queryKey = queryKeys.questions(botId);

  // State for comprehensiveness and content analysis (from generation)
  // Using query data to store this since it comes with question generation

  // Query for existing questions
  const {
    data: questions = [],
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey,
    queryFn: () => fetchQuestions(botId),
    enabled: enabled && !!botId,
    staleTime: Infinity, // Questions don't change unless we mutate them
  });

  // Store generation metadata in query cache
  const generationMetadata = queryClient.getQueryData<{
    contentAnalysis: ClarificationQuestionsResponse['data']['contentAnalysis'];
  }>([...queryKey, 'metadata']);

  // Mutation for generating questions (supports both initial and "generate more")
  const generateMutation = useMutation({
    mutationFn: (existingQuestionTexts?: string[]) =>
      generateQuestions(botId, existingQuestionTexts),
    onSuccess: (data, existingQuestionTexts) => {
      if (existingQuestionTexts && existingQuestionTexts.length > 0) {
        // "Generate more" mode: append new questions to existing ones
        queryClient.setQueryData<ClarificationQuestion[]>(queryKey, (old) => {
          if (!old) return data.displayedQuestions;
          return [...old, ...data.displayedQuestions];
        });
      } else {
        // Initial generation: replace all questions
        queryClient.setQueryData(queryKey, data.displayedQuestions);
      }

      // Store metadata (always update with latest)
      queryClient.setQueryData([...queryKey, 'metadata'], {
        contentAnalysis: data.contentAnalysis,
      });
    },
  });

  // Mutation for submitting answers
  const submitAnswerMutation = useMutation({
    mutationFn: ({
      questionId,
      answer,
      questionText,
      category,
      contentId,
    }: {
      questionId: string;
      answer: string;
      questionText: string;
      category: string;
      contentId?: string;
    }) => submitAnswer(botId, questionId, answer, questionText, category, contentId),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<ClarificationQuestion[]>(queryKey, (old) => {
        if (!old) return old;
        return old.map((q) =>
          q.id === data.questionId
            ? { ...q, status: 'answered' as const, answer: variables.answer, contentId: data.contentId }
            : q
        );
      });
    },
  });

  // Mutation for skipping questions
  const skipQuestionMutation = useMutation({
    mutationFn: (questionId: string) => skipQuestion(botId, questionId),
    onSuccess: (data) => {
      // Update question status in cache
      queryClient.setQueryData<ClarificationQuestion[]>(queryKey, (old) => {
        if (!old) return old;

        return old.map((q) =>
          q.id === data.questionId ? { ...q, status: 'skipped' as const } : q
        );
      });
    },
  });

  // Calculate summary
  const summary = calculateSummary(questions);

  // Check if all questions are resolved
  const allQuestionsResolved =
    questions.length > 0 && questions.every((q) => q.status !== 'pending');

  // Helper to submit answer - looks up question from local state
  const handleSubmitAnswer = (questionId: string, answer: string) => {
    const question = questions.find((q) => q.id === questionId);
    if (!question) {
      console.error('[useClarificationQuestions] Question not found:', questionId);
      return;
    }
    submitAnswerMutation.mutate({
      questionId,
      answer,
      questionText: question.question,
      category: question.category,
      ...(question.contentId ? { contentId: question.contentId } : {}),
    });
  };

  // Helper to reopen a resolved question (unskip or edit)
  const handleReopenQuestion = (questionId: string) => {
    queryClient.setQueryData<ClarificationQuestion[]>(queryKey, (old) => {
      if (!old) return old;

      return old.map((q): ClarificationQuestion => {
        if (q.id !== questionId) return q;
        // Destructure to remove answer property
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { answer, ...rest } = q;
        return { ...rest, status: 'pending' };
      });
    });
  };

  // "Generate more" passes existing question texts so AI avoids duplicates
  const handleGenerateMore = () => {
    const existingTexts = questions.map((q) => q.question);
    generateMutation.mutate(existingTexts);
  };

  return {
    questions,
    isLoading,
    isGenerating: generateMutation.isPending,
    error: queryError || generateMutation.error || null,
    generate: () => generateMutation.mutate(undefined),
    generateMore: handleGenerateMore,
    submitAnswer: handleSubmitAnswer,
    skipQuestion: (questionId: string) => skipQuestionMutation.mutate(questionId),
    reopenQuestion: handleReopenQuestion,
    isSubmitting: submitAnswerMutation.isPending || skipQuestionMutation.isPending,
    allQuestionsResolved,
    summary,
    contentAnalysis: generationMetadata?.contentAnalysis || null,
    hasExistingQuestions: questions.length > 0 && !generateMutation.isSuccess,
  };
}

export default useClarificationQuestions;
